import React, { useState } from 'react';
import { Button, Skeleton, Tooltip } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { AddOutlinedIcon, EditIcon, ShareIcon, ThunderIcon } from 'icons';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

import { useAppSelector } from 'store';

import { IUserProfileInfo, SubjectSelectModal } from 'modules/cabinet';
import { globalConst } from 'modules/global';

import { BallXp, ShareModal, Visible } from 'components';

import ProfilInfoEditModal from '../profil-info-edit-modal';

interface IProps {
  isNotUpdate?: boolean;
  userProfileLoading: boolean;
  userProfileInfo?: IUserProfileInfo;
}

const LeftSideProfileCard: React.FC<IProps> = ({ userProfileInfo, userProfileLoading, isNotUpdate = false }) => {
  const [open, setOpen] = useState(false);
  const [openShare, setOpenShare] = useState(false);
  const [openSubject, setOpenSubject] = useState(false);
  const loading = useAppSelector(state => state.user.loading);
  const t = useTranslations();

  if (loading && userProfileLoading) {
    return (
      <div className="rounded-xl bg-white px-4 pb-3 pt-5">
        <div className="text-end">
          <Skeleton.Button className=" !h-10 !rounded-xl" shape="square" active />
        </div>
        <div className="relative -top-7 flex flex-col items-center">
          <Skeleton.Avatar className="!h-20 !w-20" active size={80} shape="circle" />
        </div>
        <Skeleton.Input className="!mx-auto !mb-3 !h-6 !w-full" active />
        <div className="mt-3 flex justify-center gap-3">
          <Skeleton.Button className="!h-8 !w-20 !rounded-lg" active />
        </div>
        <div className="my-5 flex items-center gap-5">
          <Skeleton.Avatar size={50} className="!rounded-full" active />
          <div className="w-2/2">
            <Skeleton.Input className="!h-4 !w-28" active />
            <Skeleton.Input className="mt-2 !h-4 !w-48" active />
            <Skeleton.Button className="mt-2 !h-2 !w-full" active />
          </div>
        </div>
        <Skeleton.Input className="!mx-auto !h-4 !w-40" active />
        <div className="mt-5 flex items-center gap-3">
          <Skeleton.Button className="!h-10 !w-52 !rounded-lg" active />
          <Skeleton.Button className="!h-10 !w-14 !rounded-lg" active />
        </div>
        <Skeleton.Input className="!mx-auto mt-3 !h-4 !w-32" active />
      </div>
    );
  }

  return (
    <div className="box-border rounded-xl bg-white px-4 pb-3 pt-5">
      <Visible if={!isNotUpdate}>
        <div className="relative z-10 text-end">
          <Button onClick={() => setOpen(true)} className="!h-11 !w-11 !rounded-xl !border-none !bg-gray-100 !p-3">
            <EditIcon />
          </Button>
        </div>
      </Visible>

      {/* user profile image */}
      <div className={classNames('relative -top-5 z-0 flex flex-col items-center', { 'top-[-10px]': isNotUpdate })}>
        <div className="max-w-27 max-h-27 min-h-27 min-w-27 flex items-center justify-center rounded-full bg-gradient-to-br from-yellow-400 to-orange-600 p-0.5">
          <Image
            className="border-3 max-w-26 max-h-26 rounded-full border-white object-cover"
            width={300}
            height={300}
            alt={userProfileInfo?.full_name || 'Foydalanuvchi rasmi'}
            src={userProfileInfo?.image || '/images/default-user.png'}
            title="Foydalanuvchi rasmi"
          />
        </div>
        {userProfileInfo?.premium && (
          // eslint-disable-next-line i18next/no-literal-string
          <div className="absolute -bottom-2 rounded-full border-[0.94px] border-[#FFDD00] bg-yellow-400 px-3 py-1 text-xs font-bold text-white shadow-md">
            Akam Pro
          </div>
        )}
      </div>

      {/* User name */}
      <h3 className="text-center text-2xl font-semibold">
        {userProfileInfo?.full_name} {userProfileInfo?.last_name}
      </h3>

      {/* heart and xp */}

      <div className="mt-3 flex justify-center gap-3">
        <div className="flex items-center gap-3">
          <BallXp ball={userProfileInfo?.ball} />
        </div>
      </div>

      {(userProfileInfo?.subjects || []).length > 0 && (
        <div className="mt-4 flex flex-1 items-center gap-3 overflow-x-auto whitespace-nowrap rounded-xl border border-gray-300 p-1 px-2">
          <Visible if={!isNotUpdate}>
            <Button
              onClick={() => setOpenSubject(true)}
              className="!rounded-xl !bg-gray-50 !px-3 !py-6 hover:!border-green-500"
            >
              <AddOutlinedIcon />
            </Button>
          </Visible>
          <div className="">
            <div className="flex gap-3">
              {userProfileInfo?.subjects?.map(subject => (
                <div
                  key={subject.id}
                  className="h-13 w-13 flex items-center justify-center rounded-xl"
                  style={{ background: `${subject.color}1F` }}
                >
                  <div className="relative h-7 w-7">
                    <Image
                      src={subject.image}
                      alt={subject.name.slice(0, 1)}
                      loading="lazy"
                      fill
                      title="Subject selected list"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* level */}
      <div className="my-5 flex items-center gap-5">
        <div className="relative flex items-center justify-center w-18 h-16">
            <Image src="/images/level-badge-icon.webp" fill alt="Level badge" title="Level badge" />
          <span className="absolute text-2xl font-bold text-white">{userProfileInfo?.stage.stage}</span>
        </div>
        <div className="w-2/2">
          <p className="text-base text-green-500">
            {t('profile.left_side.user_level')}: {userProfileInfo?.stage.stage}
          </p>
          <div className="flex items-center gap-1">
            <span className="text-sm text-gray-500">{t('profile.left_side.user_next_level')}: </span>
            <div className="flex">
              <ThunderIcon />
              <span className="text-md text-blue-500">{userProfileInfo?.stage.max_xp} XP</span>
            </div>
          </div>

          <div className="mt-2 h-2 w-full rounded-full bg-gray-100">
            <Tooltip title={`${userProfileInfo?.ball} xp`}>
              <div
                className="h-2 rounded-full bg-orange-400"
                style={{
                  width: `${Math.min(
                    100,
                    ((userProfileInfo?.ball ?? 0) / (userProfileInfo?.stage?.max_xp ?? 1)) * 100
                  )}%`
                }}
              />
            </Tooltip>
          </div>
        </div>
      </div>

      {/* followers */}
      <div className="text-md flex items-center gap-5 text-green-500">
        <div>
          {userProfileInfo?.followings_count} {t('profile.left_side.obunalar')}{' '}
        </div>
        <div>
          {userProfileInfo?.followers_count} {t('profile.left_side.obunachilar')}
        </div>
      </div>

      {/* action buttons follow & share */}
      <div className="mt-5">
        <Button
          onClick={() => setOpenShare(true)}
          className="!w-full !rounded-lg !border-2 !border-gray-200 !py-6 !text-lg !font-semibold !text-green-500 hover:!border-green-500"
        >
          <ShareIcon />
          {t('profile.ulashish')}
        </Button>
      </div>

      {/* registration date */}
      <div className="mt-3 text-xs text-gray-500">
        {t('profile.left_side.Royxatdan_otilgan')}: {dayjs.unix(userProfileInfo?.created_at ?? 0).format('MMMM D YYYY')}
      </div>
      <SubjectSelectModal open={openSubject} onClose={() => setOpenSubject(false)} />
      <ShareModal
        title={t('profilni_ulashish')}
        shareUrl={`${globalConst.pageNames.CABINET}/${userProfileInfo?.id}`}
        open={openShare}
        onClose={() => setOpenShare(false)}
      />
      <ProfilInfoEditModal image={userProfileInfo?.image} open={open} onClose={() => setOpen(false)} />
    </div>
  );
};

export default LeftSideProfileCard;
